import { useMemo, useState, type CSSProperties } from "react";
import { FORTRESS_CARDS_CONFIG, type FortressCard } from "@fortress/shared";
import {
  addC,
  BASE,
  buildGrid,
  CYCLE,
  EditableGrid,
  keyC,
  mapToPattern,
  mirrorPattern,
  patternToTs,
  PatternDiagram,
  SHAPE_LABEL,
  Glyph,
} from "./fortressPattern";
import { ResourceIcon } from "../resources";

const ALL_CARDS: FortressCard[] = [
  ...(FORTRESS_CARDS_CONFIG.winCards as FortressCard[]),
  ...(FORTRESS_CARDS_CONFIG.regularCards as FortressCard[]),
];

/** A card's stored pattern → editor state (node key → shape | "marker"). */
function patternToMap(card: FortressCard): Map<string, string> {
  const m = new Map<string, string>();
  for (const b of card.pattern.buildings) m.set(keyC(addC(BASE, b.offset)), b.shape);
  for (const mk of card.pattern.markers) m.set(keyC(addC(BASE, mk)), "marker");
  return m;
}

function EditorCard({ card, grid }: { card: FortressCard; grid: ReturnType<typeof buildGrid> }) {
  const [shapes, setShapes] = useState<Map<string, string>>(() => patternToMap(card));
  const [copied, setCopied] = useState(false);

  const cycle = (key: string) =>
    setShapes((prev) => {
      const next = new Map(prev);
      const cur = next.get(key);
      const idx = CYCLE.indexOf(cur);
      const val = CYCLE[(idx + 1) % CYCLE.length];
      if (!val) next.delete(key);
      else next.set(key, val);
      return next;
    });

  const ts = patternToTs(shapes, card.pattern.bridges);
  const copy = () => {
    void navigator.clipboard?.writeText(`// ${card.id}\n${ts}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  const reset = () => setShapes(patternToMap(card));

  const mirror = useMemo(
    () => mirrorPattern(mapToPattern(shapes, card.pattern.bridges)),
    [shapes, card.pattern.bridges],
  );

  return (
    <div
      style={{
        border: "1px solid #ddd5c2",
        borderRadius: 12,
        padding: 14,
        background: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <strong style={{ fontSize: 15 }}>{card.name}</strong>
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "#888" }}
        >
          {card.kind === "win" ? "win" : "regular"} ·{" "}
          <ResourceIcon kind="obsidian" size={11} /> {card.cost[3]}
        </span>
        <button type="button" onClick={reset} style={btn}>
          reset
        </button>
        <button type="button" onClick={copy} style={{ ...btn, fontWeight: 600 }}>
          {copied ? "copied!" : "copy TS"}
        </button>
      </div>
      <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>edit</div>
          <EditableGrid grid={grid} shapes={shapes} onCycle={cycle} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>mirror</div>
          <div
            style={{
              background: "#faf6ea",
              border: "1px solid #e9e1cc",
              borderRadius: 8,
              padding: 6,
            }}
          >
            <PatternDiagram pattern={mirror} maxW={180} maxH={170} />
          </div>
        </div>
      </div>
      <pre
        style={{
          fontSize: 11,
          background: "#f6f2e6",
          padding: 8,
          borderRadius: 6,
          overflowX: "auto",
          margin: "10px 0 0",
        }}
      >
        {ts}
      </pre>
    </div>
  );
}

export function FortressEditor() {
  const grid = useMemo(() => buildGrid(4), []);

  return (
    <div
      style={{
        height: "100vh",
        overflowY: "auto",
        background: "#f2ecda",
        color: "#2b2620",
        padding: "24px 28px",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 14,
          marginBottom: 6,
          flexWrap: "wrap",
        }}
      >
        <h1 style={{ margin: 0, fontSize: 24 }}>Fortress Pattern Editor</h1>
        <span style={{ color: "#777" }}>{ALL_CARDS.length} cards</span>
        <a
          href="/library/fortresses"
          style={{ marginLeft: "auto", color: "#3f7fd0" }}
        >
          ← back to catalogue
        </a>
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 16,
          marginBottom: 6,
          fontSize: 13,
          color: "#555",
        }}
      >
        {(["scout", "barracks", "mine", "wild"] as const).map((k) => (
          <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <svg width={20} height={18} viewBox="0 0 20 18">
              <Glyph kind={k} cx={10} cy={9} />
            </svg>
            {SHAPE_LABEL[k]}
          </span>
        ))}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <svg width={20} height={18} viewBox="0 0 20 18">
            <Glyph kind="marker" cx={10} cy={9} />
          </svg>
          Marker
        </span>
      </div>
      <p style={{ color: "#555", marginTop: 0, fontSize: 13, maxWidth: 820 }}>
        <strong>Edit:</strong> click a node to cycle it (empty → scout → barracks →
        mine → wild → marker → empty). The <em>mirror</em> preview is auto — the
        matcher tries every rotation/reflection, so only the one pattern is stored.
        Then <strong>copy TS</strong> and paste the <code>pat(...)</code> over that
        card's pattern in{" "}
        <code>packages/shared/src/config/fortress.ts</code>. Edits are in-memory only.
        Bridge pieces (thick bars) can't be edited here; a card's bridges pass
        through to the copied TS unchanged.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(440px, 1fr))",
          gap: 16,
          marginTop: 14,
        }}
      >
        {ALL_CARDS.map((card) => (
          <EditorCard key={card.id} card={card} grid={grid} />
        ))}
      </div>
    </div>
  );
}

const btn: CSSProperties = {
  padding: "2px 10px",
  border: "1px solid #ccc3ac",
  borderRadius: 6,
  background: "#f6f2e6",
  color: "#2b2620",
  fontSize: 12,
  cursor: "pointer",
};
